// ==================== Types ====================

export interface RouteGroup {
  readonly pos: number
  readonly repeat: boolean
  readonly optional: boolean
}

export interface RouteRegex {
  readonly groups: Record<string, RouteGroup>
  readonly re: RegExp
  readonly namedRegex?: string
  readonly routeKeys?: Record<string, string>
}

export type RouteParams = Record<string, string | string[]>

export interface RouteMatch {
  readonly matchedRoute: string
  readonly routeParams: RouteParams
}

export type RouteMatcher = (path: string) => RouteMatch | null

interface ParsedParameter {
  readonly key: string
  readonly repeat: boolean
  readonly optional: boolean
}

interface ParameterizedRouteResult {
  readonly parameterizedRoute: string
  readonly groups: Record<string, RouteGroup>
}

interface ServerSideRouteResult extends ParameterizedRouteResult {
  readonly namedParameterizedRoute: string
  readonly routeKeys: Record<string, string>
}

// ==================== Utilities ====================

function escapeRegex(str: string): string {
  return str.replace(/[|\\{}()[\]^$+*?.-]/g, '\\$&')
}

function parseParameter(param: string): ParsedParameter {
  const optional = param.startsWith('[') && param.endsWith(']')
  if (optional) {
    param = param.slice(1, -1)
  }
  
  const repeat = param.startsWith('...')
  if (repeat) {
    param = param.slice(3)
  }
  
  return { key: param, repeat, optional } as const
}

function decodeParam(param: string): string {
  try {
    return decodeURIComponent(param)
  } catch {
    throw new Error(`Failed to decode parameter: ${param}`)
  }
}

// ==================== Route Regex Generation ====================

function getParametrizedRoute(route: string): ParameterizedRouteResult | ServerSideRouteResult {
  const segments = (route.replace(/\/$/, '') || '/').slice(1).split('/')
  
  const groups: Record<string, RouteGroup> = {}
  let groupIndex = 1
  
  const parameterizedRoute = segments
    .map((segment) => {
      if (segment.startsWith('[') && segment.endsWith(']')) {
        const { key, optional, repeat } = parseParameter(segment.slice(1, -1))
        groups[key] = { pos: groupIndex++, repeat, optional }
        return repeat ? (optional ? '(?:/(.+?))?' : '/(.+?)') : '/([^/]+?)'
      } else {
        return `/${escapeRegex(segment)}`
      }
    })
    .join('')

  // Server-side specific logic for named groups  
  if (typeof (globalThis as any).window === 'undefined') {
    let routeKeyCharCode = 97
    let routeKeyCharLength = 1

    const getSafeRouteKey = (): string => {
      let routeKey = ''
      
      for (let i = 0; i < routeKeyCharLength; i++) {
        routeKey += String.fromCharCode(routeKeyCharCode)
        routeKeyCharCode++
        
        if (routeKeyCharCode > 122) {
          routeKeyCharLength++
          routeKeyCharCode = 97
        }
      }
      
      return routeKey
    }

    const routeKeys: Record<string, string> = {}
    
    const namedParameterizedRoute = segments
      .map((segment) => {
        if (segment.startsWith('[') && segment.endsWith(']')) {
          const { key, optional, repeat } = parseParameter(segment.slice(1, -1))
          
          let cleanedKey = key.replace(/\W/g, '')
          let invalidKey = false
          
          if (cleanedKey.length === 0 || cleanedKey.length > 30) {
            invalidKey = true
          }
          
          if (!Number.isNaN(parseInt(cleanedKey.substring(0, 1)))) {
            invalidKey = true
          }
          
          if (invalidKey) {
            cleanedKey = getSafeRouteKey()
          }
          
          routeKeys[cleanedKey] = key
          
          return repeat
            ? optional
              ? `(?:/(?<${cleanedKey}>.+?))?`
              : `/(?<${cleanedKey}>.+?)`
            : `/(?<${cleanedKey}>[^/]+?)`
        } else {
          return `/${escapeRegex(segment)}`
        }
      })
      .join('')

    return {
      parameterizedRoute,
      namedParameterizedRoute,
      groups,
      routeKeys,
    } as const
  }

  return {
    parameterizedRoute,
    groups,
  } as const
}

function getRouteRegex(normalizedRoute: string): RouteRegex {
  const result = getParametrizedRoute(normalizedRoute)
  
  if ('routeKeys' in result) {
    return {
      re: new RegExp(`^${result.parameterizedRoute}(?:/)?$`),
      groups: result.groups,
      routeKeys: result.routeKeys,
      namedRegex: `^${result.namedParameterizedRoute}(?:/)?$`,
    } as const
  }

  return {
    re: new RegExp(`^${result.parameterizedRoute}(?:/)?$`),
    groups: result.groups,
  } as const
}

// ==================== Route Matching ====================

function createRouteMatcherFunc(routeRegex: RouteRegex) {
  const { re, groups } = routeRegex
  
  return (pathname: string): RouteParams | false => {
    const routeMatch = re.exec(pathname)
    if (!routeMatch) {
      return false
    }

    const params: RouteParams = {}

    for (const [slugName, group] of Object.entries(groups)) {
      const match = routeMatch[group.pos]
      if (match !== undefined) {
        params[slugName] = match.includes('/')
          ? match.split('/').map(decodeParam)
          : group.repeat
          ? [decodeParam(match)]
          : decodeParam(match)
      }
    }
    
    return params
  }
}

// ==================== Main API ====================

interface RouteEntry {
  readonly route: string
  readonly regex: RouteRegex
  readonly matcher: (pathname: string) => RouteParams | false
  readonly priority: number
}

export function createRouteMatcher(routes: readonly string[]): RouteMatcher {
  if (!Array.isArray(routes) || routes.length === 0) {
    throw new Error('Routes array cannot be empty')
  }

  const routeEntries: RouteEntry[] = routes.map((route) => {
    if (typeof route !== 'string') {
      throw new Error('All routes must be strings')
    }
    
    const regex = getRouteRegex(route)
    const matcher = createRouteMatcherFunc(regex)
    
    // Calculate priority: exact routes first, then by parameter count
    const paramCount = Object.keys(regex.groups).length
    const hasWildcard = route.includes('[...')
    const priority = hasWildcard ? -1000 - paramCount : -paramCount
    
    return {
      route,
      regex,
      matcher,
      priority,
    } as const
  })

  // Sort by priority (higher priority first)
  routeEntries.sort((a, b) => b.priority - a.priority)

  return (path: string): RouteMatch | null => {
    if (typeof path !== 'string') {
      return null
    }

    for (const entry of routeEntries) {
      const params = entry.matcher(path)
      if (params !== false) {
        return {
          matchedRoute: entry.route,
          routeParams: params,
        }
      }
    }
    
    return null
  }
}

// ==================== Exports ====================

export default createRouteMatcher

// Legacy compatibility
export { createRouteMatcher as getRouteMatcher }

// Additional utilities that might be useful
export function isValidRoute(route: string): boolean {
  try {
    getRouteRegex(route)
    return true
  } catch {
    return false
  }
}

export function getRouteParameters(route: string): string[] {
  try {
    const regex = getRouteRegex(route)
    return Object.keys(regex.groups)
  } catch {
    return []
  }
}
